import React, { useEffect, useState } from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import {
  ChartBarIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import { adminPlatformAnalyticsService, type DashboardStats, type GeographicSummary, type EngagementSummaryData, type RevenueForecastSummary } from '../../services/adminPlatformAnalyticsService';

const AdminAnalyticsPage: React.FC = () => {
  const [dashboard, setDashboard] = useState<DashboardStats | null>(null);
  const [geoSummary, setGeoSummary] = useState<GeographicSummary | null>(null);
  const [engagement, setEngagement] = useState<EngagementSummaryData | null>(null);
  const [forecast, setForecast] = useState<RevenueForecastSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('30d'); // placeholder for future filtering

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [dash, geo, eng, fore] = await Promise.all([
          adminPlatformAnalyticsService.getDashboardStats(),
          adminPlatformAnalyticsService.getGeographicSummary().catch(e => { console.warn('Geo summary failed', e); return null; }),
          adminPlatformAnalyticsService.getEngagementSummary().catch(e => { console.warn('Engagement summary failed', e); return null; }),
          adminPlatformAnalyticsService.getRevenueForecastSummary().catch(e => { console.warn('Forecast summary failed', e); return null; })
        ]);
        if (!cancelled) {
          setDashboard(dash);
          setGeoSummary(geo);
          setEngagement(eng);
            setForecast(fore);
        }
      } catch (e:any) {
        if (!cancelled) setError(e.message || 'Failed to load analytics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [timeRange]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">Loading analytics...</div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <h2 className="text-xl font-semibold mb-4">Failed to load analytics</h2>
        <p className="text-gray-600 mb-6">{error || 'Unknown error'}</p>
        <button
          onClick={() => setTimeRange(timeRange)}
          className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
        >Retry</button>
      </div>
    );
  }

  // Derived metrics for admin (aggregated approximations)
  const totalRevenue = dashboard.revenue.total;
  const churnRate = dashboard.patrons.total > 0 ? (dashboard.patrons.churnedOrPaused / dashboard.patrons.total) * 100 : 0;
  const revenueGrowthPct = forecast ? ((forecast.predictedNextMonth - forecast.currentMonthlyRevenue) / (forecast.currentMonthlyRevenue || 1)) * 100 : 0;

  const mockMonthly = forecast?.trend?.slice(0, 4)?.map((t, idx) => ({ month: `M${idx+1}`, amount: (t as any).value || (t as any).predicted || 0 })) || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="mt-2 text-gray-600">Platform performance and key metrics</p>
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="1y">Last year</option>
        </select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-sm font-medium text-green-600 flex items-center">
                <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
                {revenueGrowthPct.toFixed(1)}%
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">Total Revenue</h3>
            <p className="text-3xl font-bold text-gray-900">
              ${(totalRevenue / 1000).toFixed(1)}k
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Platform fee: ${(totalRevenue * 0.05 / 1000).toFixed(1)}k
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UsersIcon className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-blue-600 flex items-center">
                <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
                {/* Placeholder growth until endpoint supplies - using forecast confidence */}
                {(forecast?.confidenceLevel || 0).toFixed(0)}%
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">Total Users</h3>
            <p className="text-3xl font-bold text-gray-900">
              {(dashboard.patrons.total / 1000).toFixed(1)}k
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Active: {dashboard.patrons.active}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-sm font-medium text-purple-600 flex items-center">
                <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
                {/* Without creator enumeration API yet; placeholder */}
                {((dashboard.patrons.active / (dashboard.patrons.total || 1)) * 100).toFixed(1)}%
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">Active Patrons</h3>
            <p className="text-3xl font-bold text-gray-900">{dashboard.patrons.active}</p>
            <p className="text-xs text-gray-500 mt-2">
              {dashboard.patrons.total} total patrons
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <CurrencyDollarIcon className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-sm font-medium text-red-600 flex items-center">
                {churnRate.toFixed(1)}% churn
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">Recent Posts (30d)</h3>
            <p className="text-3xl font-bold text-gray-900">
              {dashboard.content.recentPosts}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {dashboard.content.totalPosts} total posts
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Revenue Trend</h2>
          <div className="h-64 flex items-end justify-around gap-4">
            {mockMonthly.map((month) => {
              const maxRevenue = Math.max(...mockMonthly.map(m => m.amount));
              const height = (month.amount / maxRevenue) * 100;
              return (
                <div key={month.month} className="flex-1 flex flex-col items-center">
                  <div className="w-full relative" style={{ height: '200px' }}>
                    <div
                      className="absolute bottom-0 w-full bg-gradient-to-t from-orange-500 to-orange-400 rounded-t-lg transition-all duration-500 hover:from-orange-600 hover:to-orange-500"
                      style={{ height: `${height}%` }}
                    >
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-sm font-medium text-gray-900">
                        ${(month.amount / 1000).toFixed(1)}k
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm font-medium text-gray-600">{month.month}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Additional Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Categories</h3>
            <div className="space-y-4">
              {(geoSummary?.topCountries?.slice(0,4) || []).map(c => (
                <div key={c.country} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{c.country}</p>
                    <p className="text-xs text-gray-500">{c.count} patrons</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">{c.percentage.toFixed(1)}%</p>
                  </div>
                </div>
              ))}
              {(!geoSummary || geoSummary.topCountries.length === 0) && (
                <p className="text-sm text-gray-500">No geographic data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Health</h3>
            <div className="space-y-4">
              {engagement && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Overall Engagement Score</span>
                  <span className="text-sm font-bold text-green-600">{engagement.overallScore.toFixed(1)}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Avg. Subscription Value</span>
                <span className="text-sm font-bold text-gray-900">${(totalRevenue / (dashboard.patrons.active || 1)).toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Churn Rate</span>
                <span className="text-sm font-bold text-red-600">{churnRate.toFixed(1)}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Forecast Confidence</span>
                <span className="text-sm font-bold text-blue-600">{forecast?.confidenceLevel ?? 0}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
